import { useState, useCallback } from 'react';

/**
 * usePolling - Generic hook for polling an endpoint until completion
 * @param {number} maxAttempts - Maximum number of polling attempts
 * @param {number} interval - Polling interval in milliseconds
 * @returns {Object} Polling state and functions
 */
export const usePolling = (maxAttempts = 60, interval = 1000) => {
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);

  const poll = useCallback(async (queryId, csrfToken) => {
    setIsPolling(true);
    setError(null);

    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`/api/services/${queryId}`, {
          headers: {
            'X-CSRF-Token': csrfToken,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch query status');
        }

        const data = await response.json();

        if (data.status === 'successful') {
          setIsPolling(false);
          if (data.outputs?.success) {
            return { success: true, ...data.outputs };
          } else {
            const errorMessage = data.outputs?.error || 'Validation failed';
            setError(errorMessage);
            return { success: false, error: errorMessage };
          }
        } else if (data.status === 'error') {
          const errorMessage = data.outputs?.error || 'Query failed';
          setError(errorMessage);
          setIsPolling(false);
          return { success: false, error: errorMessage };
        }

        // Status is 'pending' or 'processing', continue polling
        await new Promise(resolve => setTimeout(resolve, interval));
        attempts++;
      } catch (err) {
        setError(err.message);
        setIsPolling(false);
        return { success: false, error: err.message };
      }
    }

    // Timeout
    const timeoutError = 'Request timed out. Please try again.';
    setError(timeoutError);
    setIsPolling(false);
    return { success: false, error: timeoutError };
  }, [maxAttempts, interval]);

  return { isPolling, error, poll };
};
